/**
 * Feature flags whose values may vary by ARC target type. The target rows are
 * generated by jetpack_image_build/r2c2/lib.sh from target_packages.yml and
 * injected into REACT_APP_DEVICE_TYPE_ENV_MAP at webapp build time.
 */
export const DEVICE_FEATURE_KEYS = [
    "REACT_APP_HAS_NAV_AUTOMATION",
    "REACT_APP_HAS_MISSIONS",
    "REACT_APP_HAS_SCHEDULER",
    "REACT_APP_HAS_TELEGRAM_NOTIFICATION",
    "REACT_APP_ETHERNET",
    "REACT_APP_OBJ_REC",
    "REACT_APP_COMPLETE_ROBOT_PROFILE",
];

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const asString = (value) => (value === undefined || value === null ? "" : String(value));

const parseFlags = (values) => {
    const hasNavAutomation = asString(values.REACT_APP_HAS_NAV_AUTOMATION) !== "false";
    const hasMissions =
        hasNavAutomation && asString(values.REACT_APP_HAS_MISSIONS) !== "false";
    return {
        HAS_MAP_VIEW: hasNavAutomation,
        HAS_NAV_AUTOMATION: hasNavAutomation,
        HAS_NAV: hasNavAutomation,
        HAS_MISSIONS: hasMissions,
        // Opt-in: Schedule tab only when explicitly true (independent of HAS_MISSIONS).
        HAS_SCHEDULER: asString(values.REACT_APP_HAS_SCHEDULER) === "true",
        HAS_TELEGRAM_NOTIFICATION:
            asString(values.REACT_APP_HAS_TELEGRAM_NOTIFICATION) === "true",
        HAS_ETHERNET: asString(values.REACT_APP_ETHERNET) === "true",
        HAS_OBJECT_RECOGNITION: asString(values.REACT_APP_OBJ_REC) === "true",
        COMPLETE_ROBOT_PROFILE:
            asString(values.REACT_APP_COMPLETE_ROBOT_PROFILE) === "true",
    };
};

/**
 * Parse the build's direct env values while retaining which keys were explicit.
 * Explicitness matters because a direct build's selected target env overrides
 * the generated row, whereas a fleet build must use the returned target row.
 */
export const getBuildFeatureFlags = (env = process.env) => {
    const values = {};
    const explicit = {};
    DEVICE_FEATURE_KEYS.forEach((key) => {
        if (hasOwn(env, key) && env[key] !== undefined) {
            values[key] = asString(env[key]);
            explicit[key] = true;
        }
    });
    return { flags: parseFlags(values), values, explicit };
};

const parseDeviceTypeEnvMap = (env = process.env) => {
    const raw = env.REACT_APP_DEVICE_TYPE_ENV_MAP;
    if (!raw || String(raw).trim() === "") return {};
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
            throw new Error("map must be an object");
        }
        const result = {};
        Object.entries(parsed).forEach(([deviceType, row]) => {
            if (!row || typeof row !== "object" || Array.isArray(row)) return;
            const filtered = {};
            DEVICE_FEATURE_KEYS.forEach((key) => {
                if (hasOwn(row, key)) filtered[key] = asString(row[key]);
            });
            result[deviceType.trim()] = filtered;
        });
        return result;
    } catch (error) {
        if (process.env.NODE_ENV === "development") {
            console.warn("[feature-flags] invalid REACT_APP_DEVICE_TYPE_ENV_MAP:", error);
        }
        return {};
    }
};

// Exported for diagnostics/tests; runtime resolution reparses the supplied env
// so tests and embedded build tooling can pass an isolated env object.
export const DEVICE_TYPE_ENV = parseDeviceTypeEnvMap();
console.info("[feature-flags] DEVICE_TYPE_ENV:", DEVICE_TYPE_ENV);
export { parseDeviceTypeEnvMap };

const normalizeDeviceType = (deviceType) =>
    typeof deviceType === "string" ? deviceType.trim() : "";

/**
 * Resolve one ARC's effective flags.
 *
 * Fleet + known type: generated row, then defaults.
 * Direct + known type: explicit build env, then generated row, then defaults.
 * Unknown/empty type: explicit build env, then defaults.
 */
export const getFeatureFlagsForDeviceType = (
    deviceType,
    { fleetMode = false, env = process.env } = {},
) => {
    const build = getBuildFeatureFlags(env);
    const deviceMap = parseDeviceTypeEnvMap(env);
    const normalizedType = normalizeDeviceType(deviceType);
    const row = normalizedType ? deviceMap[normalizedType] : undefined;
    const hasKnownRow = Boolean(row);
    const values = {};

    DEVICE_FEATURE_KEYS.forEach((key) => {
        if (fleetMode && hasKnownRow) {
            if (hasOwn(row, key)) values[key] = row[key];
        } else if (!fleetMode && build.explicit[key]) {
            values[key] = build.values[key];
        } else if (!fleetMode && hasKnownRow && hasOwn(row, key)) {
            values[key] = row[key];
        } else if ((!fleetMode || !hasKnownRow) && build.explicit[key]) {
            values[key] = build.values[key];
        }
    });

    const resolvedFlags = parseFlags(values);
    console.info("[feature-flags] resolving device feature flags:", {
        deviceType,
        normalizedType,
        fleetMode,
        hasKnownRow,
        buildExplicitKeys: Object.keys(build.explicit),
        selectedDeviceTypeEnv: row ?? null,
        selectedValues: values,
        resolvedFlags,
    });
    return resolvedFlags;
};
